/**
 * Has two other variants - link and scroll
 */
import { Spacing } from '@persona-id/design-tokens';
import { ComponentProps, MouseEvent, useCallback } from 'react';
import styled, { css } from 'styled-components';

import {
  EditorNavItemBase,
  NAVBAR_ITEM_HEIGHT,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem';
import EditorNavItemScroll, {
  EditorNavItemScrollProps,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup/EditorNavItemScroll';
import EditorNavItemLink from 'dashboard/components/EditorNavItemLink';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconChevron from 'images/icons/chevron.svg';

export type GroupScrollItemProps = Omit<EditorNavItemScrollProps, 'activeScrollItemId'>;

const Trigger = styled(EditorNavItemLink)<{ isOpen: boolean }>`
  svg {
    height: 12px;
    transition: transform ${TransitionDuration.Fast};
    transform: rotate(90deg);
  }

  ${EditorNavItemBase} > div {
    display: flex;
  }

  &.active > * {
    color: ${({ theme }) => theme.color.text.primary};
    transition-duration: ${TransitionDuration.Fast};
  }

  // chevron rotation animation
  ${(props) =>
    props.isOpen &&
    css`
      svg {
        transition: transform ${TransitionDuration.Fast};
        transform: rotate(-90deg);
      }
    `}
`;

const ItemGroupContainer = styled('div')`
  padding-top: ${Spacing[1]};
  padding-left: ${Spacing[4]};
`;

const ScrollContainer = styled('div')`
  overflow: hidden;

  /* Needed because tooltip trigger span causes child to lose width */
  display: flex;
`;

const ScrollItem = styled(EditorNavItemScroll)`
  margin-top: 0px;
  width: 100%;
`;

interface ContainerProps {
  isOpen: boolean;
  numItems: number;
}

const Container = styled('div')<ContainerProps>`
  overflow: hidden;
  height: ${(props) =>
    props.isOpen
      ? // Trigger + Items + Padding
        NAVBAR_ITEM_HEIGHT + props.numItems * NAVBAR_ITEM_HEIGHT + BASE * 2
      : // Default height
        NAVBAR_ITEM_HEIGHT}px;

  transition: height ${TransitionDuration.Fast};
`;

interface EditorNavItemGroupProps extends ComponentProps<typeof EditorNavItemLink> {
  activeScrollItemId?: string;
  items: GroupScrollItemProps[];
  setActiveScrollItemId: (scrollItemId: string) => void;
}

/**
 * editor nav item - group variant
 */
const EditorNavItemGroup = ({
  className,
  id,
  label,
  items,
  route,
  isVisible = true,
  disabled,
  disabledLabel,
  activeScrollItemId,
  setActiveScrollItemId,
  onClick,
}: EditorNavItemGroupProps) => {
  /**
   * determine isOpen by matching activeId with ScrollItem ids in group,
   * first child id is set when opened by default
   */
  const isOpen = items.some((item) => item.id === activeScrollItemId);
  const handleOnClick = useCallback(
    (e: MouseEvent) => {
      if (disabled) {
        return;
      }

      if (isOpen) {
        e.preventDefault();
        e.stopPropagation();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setActiveScrollItemId(null);
      } else {
        // set first child item as active scroll item
        setActiveScrollItemId(items[0].id);
      }
      onClick?.(e);
    },
    [disabled, isOpen, items, onClick, setActiveScrollItemId],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Container
      className={className}
      id={`editor-nav__${id}-item-group`}
      data-test={`editor-nav__${id}-item-group`}
      numItems={items.length}
      isOpen={isOpen}
    >
      <Trigger
        id={label}
        label={label}
        route={route}
        suffixIcon={<IconChevron />}
        disabled={disabled}
        disabledLabel={disabledLabel}
        isOpen={isOpen}
        onClick={handleOnClick}
      />
      <ItemGroupContainer>
        {items.map((item) => (
          <ScrollContainer key={item.id}>
            <ScrollItem isActive={item.id === activeScrollItemId} {...item} />
          </ScrollContainer>
        ))}
      </ItemGroupContainer>
    </Container>
  );
};

export default EditorNavItemGroup;
